// PasswordResetPage.jsx
import { Check, Eye, EyeOff, Loader, Lock, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  PASSWORD_RULES,
  getPasswordValidations,
  getPasswordRuleLabel,
  isPasswordValid,
} from "../../utils/passwordPolicy";

const PasswordResetPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Redirect if no token
  useEffect(() => {
    if (!token) {
      setError("Invalid or missing reset token");
      setTimeout(() => navigate("/login"), 3000);
    }
  }, [token, navigate]);

  // Rules live in utils/passwordPolicy so this page and the "Change Password"
  // modal in Settings always enforce exactly the same thing.
  const validations = getPasswordValidations(newPassword);
  const passwordMeetsPolicy = isPasswordValid(newPassword);
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!passwordMeetsPolicy) {
      setError("Password does not meet requirements");
      return;
    }

    if (!passwordsMatch) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      const BASE_URL = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${BASE_URL}/users/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          resetToken: token,
          newPassword: newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => navigate("/login"), 3000);
      } else if (response.status === 429) {
        // Rate limited — tell the user how long to wait instead of the raw
        // server text, which used to send them chasing a new link that
        // would not have helped.
        const retryAfter =
          Number(data.retryAfterSeconds) ||
          Number(response.headers.get("Retry-After")) ||
          0;
        const minutes = Math.max(1, Math.ceil(retryAfter / 60));
        setError(
          retryAfter > 0
            ? `Too many attempts. Please wait about ${minutes} minute${minutes === 1 ? "" : "s"} and try again.`
            : "Too many attempts. Please wait a few minutes and try again."
        );
      } else {
        setError(data.error || "Failed to reset password");
      }
    } catch (err) {
      console.error("Password reset error:", err);
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundImage} />
        <div style={styles.card}>
          <div style={styles.successIcon}>
            <Check size={60} color="#10b981" />
          </div>
          <h2 style={styles.successTitle}>Password Reset Successful!</h2>
          <p style={styles.successText}>
            Your password has been changed successfully.
            Redirecting to login page...
          </p>
          <div style={styles.loaderContainer}>
            <Loader size={24} className="animate-spin" color="#3b82f6" />
          </div>
        </div>
        <style>{globalStyles}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.backgroundImage} />
      
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.logo}>
            <img src="/icon11.png" alt="Infinity Logo" style={styles.logoImg} />
          </div>
          <h2 style={styles.title}>Reset Your Password</h2>
          <p style={styles.subtitle}>Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* New Password Field */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>New Password</label>
            <div style={styles.inputWrapper}>
              <Lock style={styles.inputIcon} />
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                style={styles.input}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                style={styles.showPasswordBtn}
              >
                {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          {newPassword && (
            <div style={styles.validationBox}>
              <p style={styles.validationTitle}>Password must contain:</p>
              {PASSWORD_RULES.map((rule) => (
                <ValidationItem
                  key={rule.key}
                  valid={validations[rule.key]}
                  text={[getPasswordRuleLabel(rule, "en"), rule.hint].filter(Boolean).join(" ")}
                />
              ))}
            </div>
          )}

          {/* Confirm Password Field */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Confirm Password</label>
            <div style={styles.inputWrapper}>
              <Lock style={styles.inputIcon} />
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={styles.input}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={styles.showPasswordBtn}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {confirmPassword && !passwordsMatch && (
              <p style={styles.errorText}>Passwords do not match</p>
            )}
            {confirmPassword && passwordsMatch && (
              <p style={styles.successText}>Passwords match ✓</p>
            )}
          </div>

          {/* Error Message */}
          {error && <div style={styles.error}>{error}</div>}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !passwordMeetsPolicy || !passwordsMatch}
            style={{
              ...styles.submitBtn,
              opacity: isLoading || !passwordMeetsPolicy || !passwordsMatch ? 0.6 : 1,
              cursor: isLoading || !passwordMeetsPolicy || !passwordsMatch ? "not-allowed" : "pointer",
            }}
          >
            {isLoading ? (
              <>
                <Loader size={20} className="animate-spin" />
                Resetting Password...
              </>
            ) : (
              "Reset Password"
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate("/login")}
            style={styles.backBtn}
          >
            Back to Login
          </button>
        </form>
      </div>

      <style>{globalStyles}</style>
    </div>
  );
};

// Validation Item Component
const ValidationItem = ({ valid, text }) => (
  <div style={{
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    color: valid ? "#10b981" : "#94a3b8",
    transition: "all 0.2s",
  }}>
    {valid ? <Check size={16} /> : <X size={16} />}
    <span>{text}</span>
  </div>
);

// Styles
const styles = {
  container: {
    margin: 0,
    padding: 0,
    width: "100vw",
    height: "100vh",
    overflow: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
  },
  backgroundImage: {
    position: "absolute",
    inset: 0,
    backgroundImage: 'url("sq.png")',
    backgroundSize: "cover",
    backgroundPosition: "center",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(20px)",
    borderRadius: "24px",
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.4)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    padding: "32px",
    width: "100%",
    maxWidth: "480px",
    zIndex: 2,
    margin: "20px",
  },
  header: {
    textAlign: "center",
    marginBottom: "28px",
  },
  logo: {
    width: "120px",
    height: "120px",
    borderRadius: "50%",
    margin: "0 auto 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  logoImg: {
    width: "130%",
    height: "230%",
    objectFit: "contain",
    transform: "translate(3px, 5px)",
  },
  title: {
    fontSize: "28px",
    fontWeight: 800,
    color: "#084d98",
    margin: 0,
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
  },
  subtitle: {
    color: "#084d98",
    fontSize: "14px",
    marginTop: "6px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  fieldGroup: {
    position: "relative",
  },
  label: {
    display: "block",
    color: "#084d98",
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "8px",
  },
  inputWrapper: {
    position: "relative",
  },
  inputIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#084d98",
    width: "20px",
    height: "20px",
    pointerEvents: "none",
    zIndex: 1,
  },
  input: {
    width: "100%",
    paddingLeft: "44px",
    paddingRight: "44px",
    paddingTop: "12px",
    paddingBottom: "12px",
    background: "rgba(255, 255, 255, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "12px",
    color: "#084d98",
    fontSize: "16px",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
  },
  showPasswordBtn: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    color: "#084d98",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  validationBox: {
    background: "rgba(255, 255, 255, 0.1)",
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  validationTitle: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#084d98",
    margin: "0 0 4px 0",
  },
  error: {
    background: "rgba(254, 242, 242, 0.15)",
    border: "1px solid rgba(252, 165, 165, 0.3)",
    borderRadius: "12px",
    padding: "12px",
    color: "#ff6b6b",
    fontSize: "14px",
    textAlign: "center",
  },
  errorText: {
    color: "#ff6b6b",
    fontSize: "13px",
    marginTop: "6px",
  },
  successText: {
    color: "#10b981",
    fontSize: "13px",
    marginTop: "6px",
  },
  submitBtn: {
    width: "100%",
    background: "linear-gradient(135deg, #3b82f6, #084d98)",
    color: "white",
    padding: "12px 16px",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: 700,
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minHeight: "48px",
  },
  backBtn: {
    width: "100%",
    background: "transparent",
    color: "#084d98",
    padding: "12px 16px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 600,
    border: "1px solid #084d98",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  successIcon: {
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    background: "rgba(16, 185, 129, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
  },
  successTitle: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#084d98",
    textAlign: "center",
    marginBottom: "12px",
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
  },
};

const globalStyles = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .animate-spin {
    animation: spin 1s linear infinite;
  }
  input::placeholder {
    color: #084d98;
    opacity: 0.6;
  }
`;

export default PasswordResetPage;